import { ErrorBoundary, Shadow, Text, VStack } from '@genial/components';
import { BoxRadius, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box, Center, Spinner } from 'native-base';
import React, { ReactNode, useEffect } from 'react';
import { useWindowDimensions } from 'react-native';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { FilterModal } from '../../../../../../../components/FilterModal';
import { useRecoilQuery } from '../../../../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../../routes/types';
import { investmentStrategyFilterDiversifierAtom } from '../../../../../../../store/home/conversion/atom';
import {
  investmentStrategyCustomerSelector,
  investmentStrategyDiversifierSuitabilitySelector,
} from '../../../../../../../store/home/conversion/selector';
import { masterStateSelector } from '../../../../../../../store/master/selector';
import { sessionState } from '../../../../../../../store/session/atom';

const Wrapper: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { height } = useWindowDimensions();
  const backgroundColor = Utils.ColorToken.WithOpacity('background', 0.9);

  return (
    <Box flex={1} backgroundColor={backgroundColor} height={height}>
      <Center flex={1}>
        <Shadow level={1} paddingX={Spacing['inset.lg']} borderRadius={BoxRadius.sm}>
          {children}
        </Shadow>
      </Center>
    </Box>
  );
};

export const InvestmentStrategyFilter: React.FC = () => {
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const {
    data: masterStateData,
    loading: masterStateLoading,
    error: masterStateError,
    refresher: masterStateRefresher,
  } = useRecoilQuery(masterStateSelector({ isMaster }));
  const {
    data: suitabilityClasses,
    loading: suitabilityClassesLoading,
    error: suitabilityClassesError,
    refresher: suitabilityClassesRefresher,
  } = useRecoilQuery(investmentStrategyDiversifierSuitabilitySelector);
  const currentFilterOptions = useRecoilValue(investmentStrategyFilterDiversifierAtom);
  const setFilterOptions = useSetRecoilState(investmentStrategyFilterDiversifierAtom);
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { data: customerList } = useRecoilQuery(
    investmentStrategyCustomerSelector({
      kind: 'diversifier',
      searchTerm: currentFilterOptions.selectedFilters[1]?.filter as string,
    }),
    { if: () => !!currentFilterOptions.selectedFilters[1]?.filter }
  );

  const handleDismiss = () => {
    navigation.goBack();
  };

  const errorMessage = 'Erro ao carregar filtros';

  useEffect(() => {
    if (masterStateData && suitabilityClasses) {
      const customerListNormalized =
        customerList?.data?.map((customer) => ({
          label: customer,
          value: customer,
        })) ?? [];

      setFilterOptions((filterOptions) => ({
        ...filterOptions,
        availableFilters: [
          { type: 'date', fieldName: 'date', title: 'Data', placeholder: 'Selecione uma data' },
          {
            type: 'autocomplete',
            title: 'Cliente',
            fieldName: 'customerName',
            options: customerListNormalized,
            customAutocompleteProps: {
              maxOptionPerRender: 10,
              placeholder: 'Pesquise por nome na ordem correta',
            },
          },
          {
            type: 'select',
            title: 'Suitability',
            options: suitabilityClasses,
            fieldName: 'suitability',
          },
        ],
      }));
    }
  }, [
    customerList,
    masterStateData,
    suitabilityClasses,
    setFilterOptions,
    userProfile?.idParceiro,
  ]);

  if (masterStateError) {
    return (
      <Wrapper>
        <ErrorBoundary
          errorMessage={errorMessage}
          onCancel={handleDismiss}
          onRefresh={masterStateRefresher}
        />
      </Wrapper>
    );
  }

  if (suitabilityClassesError) {
    return (
      <Wrapper>
        <ErrorBoundary
          errorMessage={errorMessage}
          onCancel={handleDismiss}
          onRefresh={suitabilityClassesRefresher}
        />
      </Wrapper>
    );
  }

  if (masterStateLoading || suitabilityClassesLoading) {
    return (
      <Wrapper>
        <VStack space={Spacing['stack.xxxs']} marginY={Spacing['inline.xxxs']}>
          <Spinner size="lg" />
          <Text>Filtros carregando</Text>
        </VStack>
      </Wrapper>
    );
  }

  return (
    <FilterModal onClose={handleDismiss} filterAtom={investmentStrategyFilterDiversifierAtom} />
  );
};
